import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

interface DragItem {
  id: string;
  name: string;
  icon: string;
  category: string;
}

interface DropZone {
  id: string;
  name: string;
  icon: string;
  accepts: string[];
  color: string;
}

const ITEMS: DragItem[] = [
  { id: '1', name: 'Paracétamol', icon: 'ri-capsule-line', category: 'médicaments' },
  { id: '2', name: 'Amoxicilline', icon: 'ri-capsule-line', category: 'médicaments' },
  { id: '3', name: 'Ibuprofène', icon: 'ri-capsule-line', category: 'médicaments' },
  { id: '4', name: 'Stéthoscope', icon: 'ri-stethoscope-line', category: 'instruments' },
  { id: '5', name: 'Thermomètre', icon: 'ri-thermometer-line', category: 'instruments' },
  { id: '6', name: 'Seringue', icon: 'ri-syringe-line', category: 'instruments' },
  { id: '7', name: 'Cœur', icon: 'ri-heart-pulse-line', category: 'organes' },
  { id: '8', name: 'Cerveau', icon: 'ri-brain-line', category: 'organes' },
  { id: '9', name: 'Poumon', icon: 'ri-lungs-line', category: 'organes' },
  { id: '10', name: 'Pomme', icon: 'ri-apple-line', category: 'aliments' },
  { id: '11', name: 'Brocoli', icon: 'ri-leaf-line', category: 'aliments' },
  { id: '12', name: 'Eau', icon: 'ri-drop-line', category: 'aliments' },
];

const ZONES: DropZone[] = [
  { id: 'medicaments', name: 'Médicaments', icon: 'ri-capsule-line', accepts: ['médicaments'], color: 'border-red-300 bg-red-50' },
  { id: 'instruments', name: 'Instruments', icon: 'ri-stethoscope-line', accepts: ['instruments'], color: 'border-blue-300 bg-blue-50' },
  { id: 'organes', name: 'Organes', icon: 'ri-heart-pulse-line', accepts: ['organes'], color: 'border-green-300 bg-green-50' },
  { id: 'aliments', name: 'Aliments', icon: 'ri-apple-line', accepts: ['aliments'], color: 'border-amber-300 bg-amber-50' },
];

export default function DragDropGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [items] = useState(() => ITEMS.sort(() => Math.random() - 0.5));
  const [placed, setPlaced] = useState<Record<string, string>>({});
  const [dragged, setDragged] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);

  const handleStart = () => {
    setPlaced({});
    setDragged(null);
    setFeedback(null);
    setShowGameOver(false);
    startGame('drag-drop');
  };

  const handleDrop = useCallback((zoneId: string) => {
    if (!dragged) return;
    const item = ITEMS.find(i => i.id === dragged);
    if (!item) return;
    const zone = ZONES.find(z => z.id === zoneId);
    if (!zone) return;

    if (zone.accepts.includes(item.category)) {
      setPlaced(p => ({ ...p, [dragged]: zoneId }));
      updateScore(gameScore + 50);
      setFeedback(`Bravo ! ${item.name} est bien un ${zone.name.toLowerCase()}.`);
      setTimeout(() => setFeedback(null), 2000);

      if (Object.keys({ ...placed, [dragged]: zoneId }).length === ITEMS.length) {
        const finalScore = gameScore + 200;
        updateScore(finalScore);
        endGame(finalScore, gameTime, gameErrors);
        setShowGameOver(true);
      }
    } else {
      updateErrors(gameErrors + 1);
      setFeedback(`Essaie encore ! ${item.name} n'est pas dans la catégorie ${zone.name.toLowerCase()}.`);
      setTimeout(() => setFeedback(null), 2000);
    }
    setDragged(null);
  }, [dragged, placed, gameScore, gameTime, gameErrors, updateScore, updateErrors, endGame]);

  const handleReplay = () => handleStart();
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  const remainingItems = items.filter(i => !placed[i.id]);

  if (gameStatus === 'idle') {
    return (
      <GameWrapper gameName="Trie et Classe" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-sort-alphabet-asc text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Trie et Classe</h2>
            <p className="text-foreground-600 text-sm mb-6">Glisse chaque élément dans la bonne catégorie !</p>
            <button onClick={handleStart} className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-play-fill mr-2"></i> Commencer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Trie et Classe" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 max-w-5xl mx-auto w-full">
        {/* Drop zones */}
        <div className="flex-1 grid grid-cols-2 gap-3">
          {ZONES.map(zone => {
            const zoneItems = items.filter(i => placed[i.id] === zone.id);
            return (
              <div
                key={zone.id}
                className={`border-2 border-dashed rounded-2xl p-3 min-h-[120px] transition-all ${zone.color} ${dragged ? 'scale-[1.02]' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(zone.id)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${zone.icon} text-lg`}></i>
                  <h3 className="text-sm font-semibold text-foreground-950">{zone.name}</h3>
                  <span className="text-xs text-foreground-500 ml-auto">{zoneItems.length}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {zoneItems.map(item => (
                    <div key={item.id} className="flex items-center gap-1 bg-background-50/80 rounded-lg px-2 py-1 text-xs font-medium text-foreground-950">
                      <i className={`${item.icon} text-xs`}></i> {item.name}
                    </div>
                  ))}
                  {zoneItems.length === 0 && <p className="text-xs text-foreground-500 italic">Dépose ici...</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Items bank */}
        <div className="w-full lg:w-56 flex-shrink-0">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">Éléments ({remainingItems.length})</h3>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {remainingItems.map(item => (
              <div
                key={item.id}
                draggable
                onDragStart={() => setDragged(item.id)}
                onDragEnd={() => setDragged(null)}
                className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing hover:border-primary-500/50 transition-all"
              >
                <i className={`${item.icon} text-foreground-700`}></i>
                <span className="text-sm font-medium text-foreground-950">{item.name}</span>
                <i className="ri-draggable text-foreground-400 ml-auto"></i>
              </div>
            ))}
          </div>
        </div>
      </div>

      {feedback && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium animate-in shadow-lg">
          {feedback}
        </div>
      )}

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="drag-drop"
          message="Tout est bien classé !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}